/*
  XMARLIN — <xm-turntable>, the 360 viewer on the product page.

  No framework, no dependencies, one file — the same contract as
  assets/xm-carousel.js, which this deliberately does NOT reuse. A carousel is
  a strip of different pictures and the browser's scroll-snap owns it. A
  turntable is ONE picture seen from N sides: dragging must not scroll the
  page, the frames must not tween between positions, and the control a
  keyboard needs is a slider, not "previous and next slide".

  WHY NOT A LIBRARY. js-cloudimage-360-view, SpriteSpin and Magic360 all do
  this, and the owner asked. They were not used because what they add over
  this file is a dependency, not a feature: drag, momentum, keyboard, a
  scrub bar, lazy warming, wrap-around and pinch zoom are all here in ~200
  lines with no licence to track, no build step in a theme that has none, and
  no second opinion about how an <img> should be loaded. If one is ever
  wanted, the swap is this file plus the `<xm-turntable>` markup in
  sections/xm-360.liquid — nothing else in the theme knows about either.

  Behaviour worth knowing before changing it:

    - Every frame is in the DOM from the start, stacked, and only the current
      one is opaque. Swapping one <img>'s src instead shows a white gap on
      every drag until the next file arrives, which is why a spin feels
      broken on a phone.
    - The first frame loads eagerly; the rest load lazily and are then warmed
      when the browser is idle, so the second interaction is smooth without
      the first paint paying for it.
    - Horizontal drag rotates; vertical drag is left to the page. The axis is
      decided once per gesture from the first few pixels — that is what stops
      a turntable from eating a scroll on a phone.
    - A flick keeps spinning and slows down. Without it a spin stops dead
      under the finger and reads as a broken gallery rather than an object.
    - Zoom is a toggle, not a gesture, because a pinch inside a horizontal
      drag surface fights the drag. Tap (or Enter) zooms to 2x around the
      point touched; moving the pointer pans; tap again, or Escape, exits.
    - A native range input IS the control: keyboard, screen reader and voice
      control for free, in every locale, with no ARIA to get wrong. It is
      drawn as a scrub bar because that is also the clearest affordance for a
      mouse.
    - It never spins on its own, and `prefers-reduced-motion` also kills the
      momentum. An image that moves while it is being read is the thing that
      preference exists for.
*/
(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class XmTurntable extends HTMLElement {
    connectedCallback() {
      this.frames = Array.from(this.querySelectorAll('[data-xmt-frame]'));
      this.stage = this.querySelector('[data-xmt-stage]');
      if (this.frames.length < 2 || !this.stage) return;

      this.slider = this.querySelector('[data-xmt-slider]');
      this.readout = this.querySelector('[data-xmt-readout]');
      this.index = 0;
      this.zoomed = false;

      if (this.slider) {
        this.slider.max = String(this.frames.length - 1);
        this.slider.addEventListener('input', () => this.go(Number(this.slider.value)));
      }

      this.stage.addEventListener('pointerdown', this.onDown.bind(this));
      this.stage.addEventListener('pointermove', this.onMove.bind(this));
      this.stage.addEventListener('pointerup', this.onUp.bind(this));
      this.stage.addEventListener('pointercancel', this.onUp.bind(this));
      this.stage.addEventListener('pointerleave', () => { if (this.zoomed) this.setZoom(false); });
      this.stage.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.setZoom(!this.zoomed); }
        if (e.key === 'Escape' && this.zoomed) this.setZoom(false);
      });

      const prev = this.querySelector('[data-xmt-prev]');
      const next = this.querySelector('[data-xmt-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));

      this.warm();
      this.go(0);

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver((entries) => {
          const box = entries[0] && entries[0].contentRect;
          if (box && box.width) this.stageWidth = box.width;
        });
        this.ro.observe(this.stage);
      } else {
        this.resizeHandler = () => { this.stageWidth = 0; };
        window.addEventListener('resize', this.resizeHandler, { passive: true });
      }
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.ro) this.ro.disconnect();
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    }

    /* Pixels of drag per frame, from the stage width, so the same gesture
       crosses the same fraction of the spin on a phone and in a 560px
       column. The floor stops a narrow stage being hair-trigger. */
    step() {
      // The width is cached (ResizeObserver) so a pointermove never forces a
      // layout right after go() toggled the frames' classes.
      const w = this.stageWidth || this.measure();
      return Math.max(18, w / this.frames.length / 1.6);
    }

    measure() {
      this.stageWidth = this.stage.getBoundingClientRect().width;
      return this.stageWidth;
    }

    /* Fetch the rest of the spin only when it is near the viewport (or the
       visitor grabs it), and only one spin at a time: a PDP carries up to five
       and warming them all on idle was a burst of large decodes mid-scroll. */
    warm() {
      const load = () => {
        if (this.warmed) return;
        this.warmed = true;
        const run = () => {
          // Two frames at a time, the next pair once these have loaded (or
          // after 4 s), so the fetches and decodes arrive as a trickle, not
          // as one burst on the first drag.
          const done = () => { XmTurntable.warming = null; XmTurntable.pump(); };
          const frames = this.frames;
          let next = 0;
          let finished = 0;
          const loadOne = () => {
            if (next >= frames.length) return;
            const f = frames[next];
            next += 1;
            let settled = false;
            const settle = () => {
              if (settled) return;
              settled = true;
              finished += 1;
              if (finished >= frames.length) done();
              else loadOne();
            };
            if (f.complete && f.currentSrc) { f.loading = 'eager'; settle(); return; }
            f.addEventListener('load', settle, { once: true });
            f.addEventListener('error', settle, { once: true });
            window.setTimeout(settle, 4000);
            f.loading = 'eager';
          };
          loadOne();
          loadOne();
        };
        XmTurntable.queue.push(run);
        XmTurntable.pump();
      };
      this.warmNow = load;
      this.stage.addEventListener('pointerdown', load, { once: true });
      if (!('IntersectionObserver' in window)) return load();
      this.warmIo = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.warmIo.disconnect();
          if ('requestIdleCallback' in window) requestIdleCallback(load, { timeout: 2000 });
          else setTimeout(load, 300);
        }
      }, { rootMargin: '400px 0px 400px 0px' });
      this.warmIo.observe(this);
    }

    go(i) {
      const n = this.frames.length;
      const target = ((Math.round(i) % n) + n) % n;
      if (target === this.index && this.painted) return;
      this.index = target;
      this.painted = true;
      this.frames.forEach((f, k) => f.classList.toggle('is-on', k === target));
      if (this.slider && Number(this.slider.value) !== target) this.slider.value = String(target);
      if (this.readout) {
        this.readout.textContent = this.readout.dataset.template
          .replace('@deg', String(Math.round((360 / n) * target)));
      }
    }

    /* ---------------------------------------------------------------- zoom */

    setZoom(on, e) {
      this.zoomed = on;
      this.classList.toggle('is-zoomed', on);
      if (!on) {
        this.stage.style.removeProperty('--xmt-ox');
        this.stage.style.removeProperty('--xmt-oy');
        return;
      }
      if (e) this.pan(e);
    }

    pan(e) {
      const r = this.stage.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
      const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
      this.stage.style.setProperty('--xmt-ox', x + '%');
      this.stage.style.setProperty('--xmt-oy', y + '%');
    }

    /* ---------------------------------------------------------------- drag */

    onDown(e) {
      if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
      this.drag = {
        x: e.clientX, y: e.clientY, from: this.index, axis: null, id: e.pointerId,
        at: e.timeStamp, lastX: e.clientX, v: 0, moved: false,
      };
      this.classList.add('is-dragging');
    }

    onMove(e) {
      if (this.zoomed && !this.drag) { this.pan(e); return; }
      if (!this.drag || e.pointerId !== this.drag.id) return;
      const dx = e.clientX - this.drag.x;
      const dy = e.clientY - this.drag.y;

      // One decision per gesture: below the threshold we do not know yet, so
      // do nothing rather than guess and steal a scroll.
      if (this.drag.axis === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        this.drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (this.drag.axis === 'x') this.stage.setPointerCapture(e.pointerId);
      }
      if (this.drag.axis !== 'x') return;

      e.preventDefault();
      this.drag.moved = true;
      if (this.zoomed) this.setZoom(false);

      const dt = e.timeStamp - this.drag.at;
      if (dt > 0) this.drag.v = (e.clientX - this.drag.lastX) / dt; // px per ms
      this.drag.at = e.timeStamp;
      this.drag.lastX = e.clientX;

      this.go(this.drag.from + dx / this.step());
    }

    onUp(e) {
      const d = this.drag;
      this.drag = null;
      this.classList.remove('is-dragging');
      if (!d) return;

      // A press that never moved is a tap, and a tap zooms.
      if (!d.moved) { this.setZoom(!this.zoomed, e); return; }
      if (REDUCED.matches) return;
      this.glide(d.v);
    }

    /* A flick keeps turning and slows to a stop. Frames are discrete, so the
       position is carried as a float and only rounded when painted. */
    glide(v) {
      if (Math.abs(v) < 0.25) return;
      let pos = this.index;
      let speed = Math.max(-3, Math.min(3, v)) / this.step(); // frames per ms
      let last = performance.now();
      const tick = (now) => {
        const dt = now - last;
        last = now;
        pos += speed * dt;
        speed *= Math.pow(0.9955, dt); // ~ -0.45% per ms, a couple of turns at most
        this.go(pos);
        if (Math.abs(speed) * 16 < 0.02) { this.raf = null; return; }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }

  XmTurntable.queue = [];
  XmTurntable.warming = null;
  XmTurntable.pump = function () {
    if (XmTurntable.warming || !XmTurntable.queue.length) return;
    XmTurntable.warming = true;
    XmTurntable.queue.shift()();
  };

  if (!customElements.get('xm-turntable')) {
    customElements.define('xm-turntable', XmTurntable);
  }
})();
